import { LLM_API_KEY_MAX, LLM_API_KEY_MIN, LlmProviderSchema } from "@zion/sdk-contracts";
import { z } from "zod";
//#region src/internal-bootstrap.schemas.ts
/**
* Zod schemas for CP → DP `/internal/*` endpoints (see plan §7.12, §7.13.2).
*
* Single source of truth — the TypeScript DTO types in `internal-bootstrap.ts`
* are derived from these schemas via `z.infer`. Used for runtime validation by
* the DP producer tests and CP consumer tests.
*/
/** `POST /internal/long-lived-tokens` — body is empty; bearer is the bootstrap token. */
const ExchangeBootstrapRequestSchema = z.object({}).strict();
const ExchangeBootstrapResponseSchema = z.object({
	token: z.string().min(1),
	issuedAt: z.string().min(1)
}).strict();
/** `POST /internal/setup-tokens`. */
const CreateSetupTokenRequestSchema = z.object({
	token: z.string().min(1),
	expiresAt: z.string().min(1)
}).strict();
const CreateSetupTokenResponseSchema = z.object({
	name: z.string().min(1),
	namespace: z.string().min(1)
}).strict();
/** `POST /internal/operator-secrets`. */
const CreateOperatorSecretRequestSchema = z.object({
	name: z.string().min(1),
	namespace: z.string().min(1),
	data: z.record(z.string(), z.string()),
	type: z.string().min(1).optional()
}).strict();
const CreateOperatorSecretResponseSchema = z.object({
	name: z.string().min(1),
	namespace: z.string().min(1),
	resourceVersion: z.string().min(1)
}).strict();
/** `POST /internal/observer-service-accounts`. */
const CreateObserverSaRequestSchema = z.object({
	name: z.string().min(1),
	namespace: z.string().min(1)
}).strict();
const CreateObserverSaResponseSchema = z.object({
	name: z.string().min(1),
	namespace: z.string().min(1),
	tokenSecretName: z.string().min(1)
}).strict();
/** `POST /internal/admin-service-accounts`. */
const CreateAdminSaRequestSchema = z.object({
	name: z.string().min(1),
	namespace: z.string().min(1)
}).strict();
const CreateAdminSaResponseSchema = z.object({
	name: z.string().min(1),
	namespace: z.string().min(1),
	tokenSecretName: z.string().min(1)
}).strict();
/** `POST /internal/v1/llm/seed` — one credential entry (S6 / DEC-31). */
const LlmSeedCredentialSchema = z.object({
	provider: LlmProviderSchema,
	apiKey: z.string().min(LLM_API_KEY_MIN).max(LLM_API_KEY_MAX)
}).strict();
/**
* `POST /internal/v1/llm/seed` — request body (S6 / DEC-31). Seeds the org's LLM keys once after the
* DP is READY (setup-token push pattern; CP persists no plaintext, DEC-9). `orgId` is the target
* tenant (provisioning validated it ∈ assigned tenants before sending — DEC-15); ≥1 credential
* required. Idempotent upsert server-side (WS-2).
*/
const CreateLlmSeedRequestSchema = z.object({
	orgId: z.string().uuidv4(),
	credentials: z.array(LlmSeedCredentialSchema).min(1)
}).strict();
/** `POST /internal/v1/llm/seed` — 2xx response: count of (org,provider) rows upserted. */
const CreateLlmSeedResponseSchema = z.object({ seeded: z.number().int().nonnegative() }).strict();
/** `DELETE /internal/secrets/:name?namespace=`. */
const DeleteSecretParamsSchema = z.object({
	name: z.string().min(1),
	namespace: z.string().min(1)
}).strict();
/** `GET /internal/ingress-address`. */
const IngressAddressResponseSchema = z.object({
	ipAddress: z.string().nullable(),
	hostname: z.string().nullable(),
	ready: z.boolean()
}).strict();
/** `GET /internal/observability/pods?namespace=&labelSelector=`. */
const ListPodsQuerySchema = z.object({
	namespace: z.string().min(1),
	labelSelector: z.string().optional()
}).strict();
/**
* The container's PREVIOUS state, when it has one (`status.containerStatuses[].lastState`).
*
* This is the "why did it restart" record. k8s populates `lastState` only after a
* container has terminated at least once; a never-restarted container carries
* `lastState: {}`, which the producer MUST render as an ABSENT `lastState` rather
* than an empty object (D2: present ⇒ meaningful).
*
* `state` mirrors the sibling `PodContainerStatus.state` vocabulary and is
* deliberately `z.string().min(1)`, NOT an enum — the producer derives it with the
* same `derivePodContainerState` helper, and a future k8s state name must not make
* a whole pod payload unparseable (the `image` precedent at
* `PodContainerStatusSchema.image`).
*/
const PodContainerLastStateSchema = z.object({
	/** `running` | `waiting` | `terminated`. Never `unknown` — see the omit rule above. */
	state: z.string().min(1),
	/**
	* `lastState.<state>.reason` — e.g. `OOMKilled`, `Error`, `Completed`.
	* Absent when kubelet reported no reason for the previous state.
	*
	* `.max(256)` documents the expected bound on a DP-supplied string — real kubelet
	* reasons are short CamelCase tokens, and `.min(1)` without a `.max()` is a
	* half-constraint (sibling precedents: `app-factory-ca.schemas.ts:17`,
	* `instance-identity.schemas.ts:18-35`, `branding-sync.schemas.ts:18`).
	*
	* ⛔ IT IS NOT A RUNTIME CONTROL, AND MUST NOT BE CITED AS ONE. Nothing parses
	* this response at runtime: there is no DP egress validation (core-api's only
	* global pipe is request-INPUT validation — no response serializer, no
	* `useGlobalInterceptors`), no CP ingress validation (`DpHttpClient.request` is a
	* bare `JSON.parse(text) as T`), and no response size cap on `response.text()`.
	* So a skewed or compromised DP CAN push an arbitrarily long `reason` into a CP
	* render surface today; this bound will not stop it. The enforcement that does
	* exist is TEST-TIME only — see the matching note at
	* `core-api/.../observability-k8s.client.ts` ("NOTHING parses this response at
	* runtime"), which is the accurate statement of the same fact.
	*
	* ⚠️ On a RESPONSE schema a `.max()` is documentation; the identical syntax on a
	* REQUEST schema is enforced by the controller's validation pipe. Check which
	* side of the seam you are reading before treating a bound as a control.
	*/
	reason: z.string().min(1).max(256).optional(),
	/**
	* `lastState.terminated.exitCode`. Absent unless the previous state was
	* `terminated`. ⚠️ `0` is a MEANINGFUL value (clean exit) — consumers MUST
	* discriminate with `!== undefined`, never with truthiness or `||`.
	*/
	exitCode: z.number().int().optional(),
	/**
	* `lastState.terminated.finishedAt`, forwarded verbatim as the ISO string k8s
	* reports. Absent unless the previous state was `terminated` and k8s reported it.
	*
	* `.max(64)` — an ISO-8601 instant is ~20–30 characters; 64 is generous headroom
	* and still bounds an untrusted string. Deliberately NOT `z.iso.datetime()`: the
	* producer forwards what k8s reported and a format surprise must not make a whole
	* pod payload unparseable (the `image` precedent).
	*/
	finishedAt: z.string().min(1).max(64).optional()
}).strict();
const PodContainerStatusSchema = z.object({
	name: z.string().min(1),
	ready: z.boolean(),
	restartCount: z.number().int().nonnegative(),
	state: z.string().min(1),
	/**
	* Fully-qualified container image ref **including tag/digest**
	* (e.g. `zion-registry:5000/zion/core-api:1.29.29`), un-stripped from the
	* pod's `status.containerStatuses[].image` (WS2). The frontend parses the
	* tag as the per-pod version **fallback** when `attribution.version` is
	* absent (DECISIONS A3). Absent or empty when k8s has not reported it —
	* this MUST NOT reject the pod payload (an unstarted/pending container is
	* valid); an empty/absent image simply yields no version parse, which the
	* frontend cascade treats as fail-safe `upgrading` (A3/T7).
	*/
	image: z.string().optional(),
	/**
	* The CURRENT state's reason — `state.waiting.reason` (e.g. `ContainerCreating`,
	* `CrashLoopBackOff`, `ImagePullBackOff`) or `state.terminated.reason` (e.g.
	* `OOMKilled`, `Completed`, `Error`).
	*
	* ⚠️ ABSENT IS THE NORMAL, HEALTHY CASE: `state.running` has no `reason` field at
	* all, so every healthy running container omits this. Absence is NOT an error and
	* MUST NOT be rendered as one. Consumers fall back to the `state` name.
	*
	* This is the field three CP consumers were already written against and never
	* received — see WS-A plan §5.1.
	*
	* `.max(256)` for the same reason as `PodContainerLastStateSchema.reason`: this
	* string is DP-supplied, is rendered in a CP pill, and needs an upper bound as
	* well as a lower one.
	*/
	reason: z.string().min(1).max(256).optional(),
	/**
	* `state.terminated.exitCode` — the CURRENT state's exit code.
	*
	* ABSENT whenever the container is not presently terminated (running or waiting),
	* which is the normal case. ⚠️ `0` is MEANINGFUL (clean exit) and is NOT the same
	* as absent: `exitCode: 0` means "exited successfully", absent means "has not
	* exited". Consumers MUST discriminate with `!== undefined`. `z.number().int()`
	* with NO `.nonnegative()` — a container killed by signal N reports `128+N`, and
	* while k8s does not emit negatives, constraining it buys nothing and risks
	* rejecting a whole pod payload.
	*/
	exitCode: z.number().int().optional(),
	/**
	* The previous state (see `PodContainerLastStateSchema`).
	*
	* ABSENT ⇔ the container has never previously terminated. Expected invariant:
	* `restartCount === 0` ⇒ `lastState` absent; `restartCount > 0` ⇒ `lastState`
	* present. Absence is the normal case for the majority of containers.
	*/
	lastState: PodContainerLastStateSchema.optional()
}).strict();
/**
* Distilled pod attribution (DECISIONS A2 / D3 / S1). A typed projection of the
* FOUR operator-stamped label keys — and ONLY those four — so per-pod
* app/service/version attribution can reach the CP UI without forwarding the raw
* `metadata.labels` map (which could leak arbitrary/PII annotations, threat T1).
*
* Field ← label key (WS2 mapping):
*   serviceName ← `app.kubernetes.io/name`   (core DP service, e.g. "core-api")
*   app         ← `zion.witness.ai/app`       (owning app id)
*   fixture     ← `zion.witness.ai/fixture`   (owning fixture id)
*   version     ← `zion.witness.ai/version`   (per-pod version slug; preferred
*                                              version source, DECISIONS A3)
*
* `.strict()` is the S1 control at the type level: the contract is structurally
* incapable of carrying any label key beyond these four. Every field is optional
* — a pod may carry none of the keys (an unattributed namespace pod → the UI's
* "Other pods" group, D10). Producers include a key ONLY when its label is
* present, and MUST omit (not emit empty) — `present ⇒ non-empty real value`.
*/
const PodAttributionSchema = z.object({
	app: z.string().min(1).optional(),
	fixture: z.string().min(1).optional(),
	version: z.string().min(1).optional(),
	serviceName: z.string().min(1).optional()
}).strict();
const PodSummarySchema = z.object({
	name: z.string().min(1),
	namespace: z.string().min(1),
	phase: z.string().min(1),
	containerStatuses: z.array(PodContainerStatusSchema),
	/**
	* Distilled attribution (see `PodAttributionSchema`). **`.default({})`**
	* (ruling R-CONTRACT1), NOT bare `.optional()`: an OLD DP payload that
	* predates this field must still parse under a NEW CP (deploy skew), and
	* defaulting to `{}` on absent means consumers read `attribution.version`
	* etc. WITHOUT null-checking the object. `{}` (no known labels) ⇒ the
	* frontend treats the pod as "unattributed" (D10). NEVER the raw label map.
	*/
	attribution: PodAttributionSchema.default({}),
	/**
	* Real pod age in whole seconds, un-stripped by WS2 (derived by the core-api
	* producer's `computeAgeSeconds` from `metadata.creationTimestamp`). The
	* contract carried NO age before this change, which is why provisioning-svc
	* hardcoded `ageSeconds: 0`. Absent ⇒ consumer coalesces to 0.
	*/
	ageSeconds: z.number().int().nonnegative().optional(),
	/**
	* `spec.nodeName` — the node this pod is scheduled on.
	*
	* ⚠️ ABSENT IS A REAL AND DIAGNOSTIC STATE, not an error: an unscheduled pod
	* (`phase: 'Pending'`, e.g. unschedulable for want of capacity) has no node yet,
	* and the absence is itself the signal. Also absent on deploy skew (a DP that
	* predates this field).
	*
	* SECURITY (mirrors the S1/A2 posture on `attribution`): this is a single
	* whitelisted SCALAR read out of `spec`. The producer MUST NOT forward `pod.spec`
	* or any other part of it — `spec` carries env vars, volume names and image pull
	* secrets. One field, by name, or nothing.
	*
	* `.max(256)` — a k8s node name is a DNS subdomain (253 chars max), so 256 is the
	* natural bound and it keeps a DP-supplied string from blowing out the CP render.
	*/
	nodeName: z.string().min(1).max(256).optional(),
	/**
	* compute-classes D7 — TRUE iff this pod carries a `PodScheduled=False` condition with
	* `reason: 'Unschedulable'`.
	*
	* ⭐ WHY A BOOLEAN AND NOT `conditions[]`. The app-facing `POD_PROBLEMS` enum in
	* `@zion/sdk-contracts` deliberately omitted `'Unschedulable'` with the recorded reason that it
	* *"lives on the pod condition, and `conditions[]` is not on `PodSummarySchema` at all — only
	* `phase` is"*, i.e. adding the enum member without a producer would have created *"an enum
	* member nothing can ever emit."* This field is that producer, in the narrowest possible shape.
	*
	* ⛔ DO NOT "COMPLETE" THIS INTO A `conditions[]` ARRAY. The existing `nodeName` field records
	* the posture: ONE whitelisted scalar read out of the pod object, by name, or nothing —
	* forwarding `pod.status.conditions` would ship condition MESSAGES, which are free-text from the
	* cluster autoscaler and are exactly the untrusted strings the scaling-event contract bounds at
	* 512 **UTF-16 CODE UNITS** before they reach a log or a query. One boolean carries the whole
	* signal an app-facing screen needs; the message belongs on a scaling event, which already
	* bounds it (`@zion/sdk-contracts` `scaling-event.ts:32,34` — `SCALING_TEXT_MAX = 512` used as
	* `z.string().max(SCALING_TEXT_MAX)`; this file cites it by description, so the location is here
	* to save the next reader the grep).
	* ⛔ **CODE UNITS, NOT BYTES — corrected 2026-08-14, and it makes this argument STRONGER, not
	* weaker.** `.max()` counts `String.prototype.length`, so the byte ceiling is **3×**: a BMP
	* character is 1 code unit and up to 3 UTF-8 bytes (an astral character is 2 code units and 4
	* bytes, i.e. only 2 bytes/unit), so one bounded field admits up to **1 536 bytes**, not 512.
	* ⇒ The untrusted strings this docblock declines to forward are up to 3× larger than the
	* sentence used to claim. **Do not read the correction as licence to relax the posture:** the
	* bound being looser than believed is more reason not to put autoscaler free-text on an
	* app-facing surface, not less.
	*
	* OPTIONAL, and ABSENT IS NOT `false` — it is "this DP predates the field" (deploy skew). A
	* consumer that coalesces absent to `false` is making the same mistake `ageSeconds`' docblock
	* warns about (coalescing to `0` renders a week-old pod as brand new). Render absence as "not
	* reported", not as "schedulable".
	*
	* ⛔ ORDERING — CP FIRST, same as `StatusReport.computeResolutions`.
	*/
	unschedulable: z.boolean().optional()
}).strict();
const ListPodsResponseSchema = z.object({ items: z.array(PodSummarySchema) }).strict();
/**
* `GET /internal/observability/logs?app=&query=&since=&start=&end=&limit=`.
*
* Stream selection: either `app` (a short app-name mapped DP-side into an
* `{app="<app>"}` stream selector) OR `query` (a raw LogQL expression passed
* through verbatim). The controller requires at least one.
*
* Time window — THREE parameters, deliberately, for deploy skew (D2). A DP runs
* a different release than the CP talking to it:
*   `since`  — legacy. A duration (`5m`, `1h`) or an absolute instant. Converted
*              to Loki's `start` as `now - duration` when it is a duration
*              (`toLokiStart`). Carries NO upper bound, so it can only ever
*              express a window ending "now".
*   `start`  — NEW. An absolute instant, in EITHER of the two forms the DP's
*              own sink accepts: an RFC3339 / ISO-8601 timestamp, OR a bare
*              non-negative integer epoch. Takes PRECEDENCE over `since` on a
*              DP that understands it; a DP that does not ignores the unknown
*              query param and honours `since` (§1.4).
*              ⛔ BOTH forms are real traffic, not theory: the site-admin Logs
*              tab pages by putting its cursor in this field, and the cursor is
*              a 19-digit NANOSECOND epoch integer — `cursor ?? start` at
*              `loki-proxy-client.impl.ts`, minted as
*              `(maxTsNano + 1n).toString()`. `Date.parse` returns NaN for it.
*              ⇒ Never REJECT the integer form: a validator that 400s it breaks
*              every cursor-paged Logs request (R2-08).
*
*              ⚠️ "Never reject" is NOT "always forward", and this line used to
*              say "do NOT narrow this to ISO-only anywhere on the path" — which,
*              followed literally, re-opens R2-08 from the other side. The CP's
*              `LokiProxyService` DELIBERATELY narrows to ISO here (`isIsoInstant`
*              returns false for a bare integer) and DROPS the cursor rather than
*              forwarding it, because forwarding `newest+1ns` as an absolute
*              `start` under `direction: backward` makes "Load more" return zero
*              rows. Dropping falls back to `since`, which is what R2 shipped.
*              Correct paging means putting the cursor on `end`, not `start` —
*              a live-surface change, deliberately not made here.
*              ⇒ The rule is per-hop, not global: the CONTRACT and the DP SINK
*              accept both forms; an intermediate hop may narrow what it forwards
*              so long as its forward-set stays a SUBSET of the sink's accept-set.
*   `end`    — NEW. Same accept-set as `start`; the window's upper bound.
*              Absent ⇒ Loki's default of "now".
* Producers MUST send `since` alongside `start`/`end` so an un-rolled DP still
* receives a window rather than falling back to its own default. This is why
* `since` is not deprecated here.
*/
const GetLogsQuerySchema = z.object({
	app: z.string().min(1).optional(),
	query: z.string().min(1).optional(),
	since: z.string().optional(),
	start: z.string().min(1).optional(),
	end: z.string().min(1).optional(),
	limit: z.number().int().positive().optional(),
	/**
	* Loki scan direction. Absent ⇒ the DP's default, which is `backward`
	* (newest-first) — `loki-http.client.ts` hardcoded that literal before R4.
	*
	* ⛔ R4 owns this field. R3 ruling **R-20** ASSIGNED it here — *assigned, not
	* deferred* — and deliberately shipped R3 WITHOUT it, because at that point
	* `direction` had no producer anywhere in the CP chain (absent from
	* `LokiQueryParams`, `LokiProxyController`, `LokiProxyClient.queryRange` and
	* `LogQueryParams`), and an optional field with no producer-side assertion is
	* the exact defect D2 forbids. R4 supplies the producer, so the field lands
	* with both sides asserted.
	*
	* ⚠️ THIS IS THE CP↔DP WIRE FIELD. There is a SECOND, DIFFERENT `direction`
	* on the CP's public app-logs query (`@zion/sdk-contracts` `app-logs.ts`).
	* They are separate fields in separate packages and must not be conflated —
	* the public one is what an app owner sends; this one is what CP forwards to
	* the DP after its own validation.
	*
	* Enum order is `['forward','backward']` deliberately: zod echoes the option
	* list in its 400 message, and this ordering matches the CP-side literal so
	* the two error strings read identically.
	*/
	direction: z.enum(["forward", "backward"]).optional()
}).strict();
const LogEntrySchema = z.object({
	timestamp: z.string().min(1),
	line: z.string(),
	labels: z.record(z.string(), z.string())
}).strict();
const GetLogsResponseSchema = z.object({ entries: z.array(LogEntrySchema) }).strict();
/**
* DNS-1123 *label* — k8s container names. Max 63, no dots.
* The `.max()` check is declared BEFORE `.regex()` so input length is bounded
* before the match runs (the repo's ReDoS rule).
*/
const DNS_1123_LABEL = /^[a-z0-9]([-a-z0-9]*[a-z0-9])?$/;
/**
* `GET /internal/observability/pods/:podName/log?namespace=&container=&previous=&tailLines=`.
*
* The one question Loki structurally cannot answer: what a container printed
* before it died, and what the PREVIOUS instance of a restarting container
* printed. Loki may never have received those lines (no flush before the crash)
* and has no notion of a prior container instance.
*
* NOT a stream. One-shot and bounded — `follow` is deliberately absent; live
* tail is a different mechanism on a different transport.
*
* `podName` is a path param (`GetPodLogParamsSchema`), not part of this object.
*/
const GetPodLogParamsSchema = z.object({ podName: z.string().min(1).max(253).regex(/^[a-z0-9]([-a-z0-9]*[a-z0-9])?(\.[a-z0-9]([-a-z0-9]*[a-z0-9])?)*$/) }).strict();
const GetPodLogQuerySchema = z.object({
	namespace: z.string().min(1),
	/**
	* Omitted ⇒ the DP resolves it: exactly one container in the pod is used,
	* more than one is a 400 naming the choices. The DP NEVER guesses.
	*/
	container: z.string().min(1).max(63).regex(DNS_1123_LABEL).optional(),
	/** Read the terminated PREVIOUS instance. Absent ⇒ false. */
	previous: z.boolean().optional(),
	/** Lines from the end. Absent ⇒ 500. Bounded [1, 2000]. */
	tailLines: z.number().int().min(1).max(2e3).optional()
}).strict();
const GetPodLogResponseSchema = z.object({
	/** Echoed back so a client that fanned out can attribute the response. */
	podName: z.string().min(1),
	/** The RESOLVED container — never absent, even when the request omitted it. */
	container: z.string().min(1),
	/** The EFFECTIVE value, so `false` is affirmative and not merely "unset". */
	previous: z.boolean(),
	/**
	* Log lines, oldest first, WITHOUT trailing newlines. An empty array means
	* the container has printed nothing — it is never a stand-in for an error.
	* Every failure on this path is an HTTP error with a message (D5 posture,
	* and the D6 anti-pattern this endpoint must not repeat).
	*/
	lines: z.array(z.string()),
	/**
	* TRUE ⇒ output was cut at `tailLines` and older lines exist. Determined
	* EXACTLY, not by `lines.length === tailLines`: the DP asks k8s for
	* `tailLines + 1` and truncation is `received > tailLines`.
	*/
	truncated: z.boolean()
}).strict();
/**
* Allowlist of Kubernetes resource kinds that may be listed through
* `GET /internal/observability/resources`. Enforced schema-level so requests
* outside this set are rejected at parse time (the controller additionally
* returns `FORBIDDEN_RESOURCE_KIND` for consistency with the error envelope).
*/
const ResourceKindSchema = z.enum([
	"Pod",
	"Deployment",
	"Service",
	"Job",
	"CronJob",
	"Gateway",
	"HTTPRoute"
]);
/** `GET /internal/observability/events?namespace=&involvedObjectName=&since=&limit=`. */
const ListEventsQuerySchema = z.object({
	namespace: z.string().min(1),
	involvedObjectName: z.string().min(1).optional(),
	since: z.string().min(1).optional(),
	limit: z.number().int().positive().optional()
}).strict();
const EventInvolvedObjectSchema = z.object({
	kind: z.string().min(1),
	name: z.string().min(1)
}).strict();
const EventSummarySchema = z.object({
	type: z.string().min(1),
	reason: z.string().min(1),
	message: z.string(),
	firstTimestamp: z.string().nullable(),
	lastTimestamp: z.string().nullable(),
	count: z.number().int().nonnegative(),
	involvedObject: EventInvolvedObjectSchema
}).strict();
const ListEventsResponseSchema = z.object({ items: z.array(EventSummarySchema) }).strict();
/** `GET /internal/observability/resources?namespace=&kind=&labelSelector=`. */
const ListResourcesQuerySchema = z.object({
	namespace: z.string().min(1),
	kind: ResourceKindSchema,
	labelSelector: z.string().min(1).optional()
}).strict();
const ListResourcesResponseSchema = z.object({ items: z.array(z.record(z.string(), z.unknown())) }).strict();
/**
* Aggregated registry of schemas for the CP → DP internal-bootstrap surface.
* Consumers (`DpHttpClient` contract test, core-api producer test) look up
* schemas by endpoint rather than importing each schema symbol individually.
*/
const InternalBootstrapSchemas = {
	ExchangeBootstrapRequest: ExchangeBootstrapRequestSchema,
	ExchangeBootstrapResponse: ExchangeBootstrapResponseSchema,
	CreateSetupTokenRequest: CreateSetupTokenRequestSchema,
	CreateSetupTokenResponse: CreateSetupTokenResponseSchema,
	CreateOperatorSecretRequest: CreateOperatorSecretRequestSchema,
	CreateOperatorSecretResponse: CreateOperatorSecretResponseSchema,
	CreateObserverSaRequest: CreateObserverSaRequestSchema,
	CreateObserverSaResponse: CreateObserverSaResponseSchema,
	CreateAdminSaRequest: CreateAdminSaRequestSchema,
	CreateAdminSaResponse: CreateAdminSaResponseSchema,
	DeleteSecretParams: DeleteSecretParamsSchema,
	IngressAddressResponse: IngressAddressResponseSchema,
	ListPodsQuery: ListPodsQuerySchema,
	ListPodsResponse: ListPodsResponseSchema,
	GetLogsQuery: GetLogsQuerySchema,
	GetLogsResponse: GetLogsResponseSchema,
	GetPodLogParams: GetPodLogParamsSchema,
	GetPodLogQuery: GetPodLogQuerySchema,
	GetPodLogResponse: GetPodLogResponseSchema,
	ListEventsQuery: ListEventsQuerySchema,
	ListEventsResponse: ListEventsResponseSchema,
	ListResourcesQuery: ListResourcesQuerySchema,
	ListResourcesResponse: ListResourcesResponseSchema,
	CreateLlmSeedRequest: CreateLlmSeedRequestSchema,
	CreateLlmSeedResponse: CreateLlmSeedResponseSchema
};
//#endregion
export { CreateAdminSaRequestSchema, CreateAdminSaResponseSchema, CreateLlmSeedRequestSchema, CreateLlmSeedResponseSchema, CreateObserverSaRequestSchema, CreateObserverSaResponseSchema, CreateOperatorSecretRequestSchema, CreateOperatorSecretResponseSchema, CreateSetupTokenRequestSchema, CreateSetupTokenResponseSchema, DeleteSecretParamsSchema, ExchangeBootstrapRequestSchema, ExchangeBootstrapResponseSchema, GetLogsQuerySchema, GetLogsResponseSchema, GetPodLogParamsSchema, GetPodLogQuerySchema, GetPodLogResponseSchema, IngressAddressResponseSchema, InternalBootstrapSchemas, ListEventsQuerySchema, ListEventsResponseSchema, ListPodsQuerySchema, ListPodsResponseSchema, ListResourcesQuerySchema, ListResourcesResponseSchema, LlmSeedCredentialSchema, PodAttributionSchema, PodContainerLastStateSchema, PodContainerStatusSchema, PodSummarySchema, ResourceKindSchema };

//# sourceMappingURL=internal-bootstrap.schemas.js.map